import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { ArrowUpRight, Bed, PartyPopper, UtensilsCrossed, Building2, MapPin } from "lucide-react";

const TABS = [
  { key: "hotels", label: "Hotels", metric: null, icon: Building2, hint: "All properties, newest first" },
  { key: "rooms", label: "Rooms", metric: "rooms", icon: Bed, hint: "Ranked by total rooms" },
  { key: "banquets", label: "Banquets", metric: "banquet_halls", icon: PartyPopper, hint: "Ranked by banquet halls" },
  { key: "restaurants", label: "Restaurants", metric: "restaurants", icon: UtensilsCrossed, hint: "Ranked by F&B outlets" },
];

const PLACEHOLDER = "https://images.pexels.com/photos/164586/pexels-photo-164586.jpeg?auto=compress&cs=tinysrgb&w=800";

export default function BrowsePage() {
  const { tab: tabParam } = useParams();
  const tab = TABS.find((t) => t.key === tabParam) || TABS[0];
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (tab.metric) {
      api
        .listHotels({ sort: tab.metric, limit: 200 })
        .then(setHotels)
        .finally(() => setLoading(false));
    } else {
      api.listHotels({ limit: 200 }).then(setHotels).finally(() => setLoading(false));
    }
  }, [tab.key, tab.metric]);

  const total = useMemo(() => {
    if (!tab.metric) return hotels.length;
    return hotels.reduce((acc, h) => acc + Number(h[tab.metric] || 0), 0);
  }, [hotels, tab.metric]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <div className="mb-8">
        <div className="font-label text-xs text-[var(--brand-blue)] mb-3">Browse Directory</div>
        <h1 data-testid="browse-title" className="font-display text-4xl sm:text-6xl leading-none">{tab.label}.</h1>
        <p className="mt-3 text-sm text-[var(--ink-soft)]">{tab.hint}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--line)] mb-8 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.key}
                to={`/browse/${t.key}`}
                data-testid={`browse-tab-${t.key}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-4 sm:px-5 py-3 font-display text-sm border-b-2 -mb-px transition-colors ${
                    isActive
                      ? "border-[var(--brand-blue)] text-[var(--ink)]"
                      : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </NavLink>
            );
          })}
        </div>
      </div>

      {/* Metric summary */}
      {tab.metric && (
        <div data-testid="browse-total" className="mb-6 flex items-baseline gap-3">
          <span className="font-display text-4xl sm:text-5xl leading-none">{total.toLocaleString()}</span>
          <span className="font-label text-[10px] text-[var(--ink-soft)]">Total {tab.label.toLowerCase()} across all hotels</span>
        </div>
      )}

      {loading ? (
        <div className="text-[var(--ink-soft)] font-label text-sm">Loading…</div>
      ) : hotels.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--line)] p-10 text-center">
          <h3 className="font-display text-3xl mb-2">Nothing here yet.</h3>
          <p className="text-sm text-[var(--ink-soft)]">Add hotels via admin to populate this leaderboard.</p>
        </div>
      ) : (
        <div className="border border-[var(--line)] overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[var(--paper-2)] border-b border-[var(--line)]">
              <tr className="font-label text-[10px] text-[var(--ink-soft)]">
                <Th>#</Th>
                <Th>Hotel</Th>
                <Th>Location</Th>
                <Th className="text-right">Rooms</Th>
                <Th className="text-right">Banquets</Th>
                <Th className="text-right">F&B</Th>
                <Th className="text-right">Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {hotels.map((h, idx) => (
                <tr key={h.id} data-testid={`browse-row-${h.id}`} className="border-b border-[var(--line)] hover:bg-[var(--paper-2)]">
                  <td className="p-3 font-label text-[10px] text-[var(--ink-soft)]">{String(idx + 1).padStart(2, "0")}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={h.image_url ? (h.image_url.startsWith("http") ? h.image_url : fileUrl(h.image_url)) : PLACEHOLDER}
                        alt=""
                        className="w-10 h-10 object-cover border border-[var(--line)]"
                        onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                      />
                      <div>
                        <div className="font-display text-base leading-tight">{h.name}</div>
                        <div className="text-xs text-[var(--ink-soft)]">{h.city}, {h.state}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[var(--ink-soft)] max-w-[220px] truncate">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{h.location}</span>
                  </td>
                  <MetricCell value={h.rooms} highlighted={tab.metric === "rooms"} />
                  <MetricCell value={h.banquet_halls} highlighted={tab.metric === "banquet_halls"} />
                  <MetricCell value={h.restaurants} highlighted={tab.metric === "restaurants"} />
                  <td className="p-3 text-right">
                    <StatusBadge status={h.status} />
                  </td>
                  <td className="p-3 text-right">
                    <Link
                      to={`/state/${encodeURIComponent(h.state)}/city/${encodeURIComponent(h.city)}`}
                      data-testid={`browse-view-${h.id}`}
                      className="inline-flex items-center gap-1 font-label text-[10px] hover:text-[var(--brand-blue)]"
                    >
                      Open <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left p-3 font-normal ${className}`}>{children}</th>;
}

function MetricCell({ value, highlighted }) {
  return (
    <td className={`p-3 text-right font-display ${highlighted ? "text-[var(--brand-blue)] text-lg" : "text-base"}`}>
      {value ?? 0}
    </td>
  );
}

function StatusBadge({ status }) {
  const upcoming = status === "upcoming";
  return (
    <span
      className={`inline-block px-2 py-0.5 font-label text-[9px] tracking-wider ${
        upcoming
          ? "bg-[var(--brand-orange)] text-white"
          : "bg-[var(--paper-3)] text-[var(--ink)]"
      }`}
    >
      {upcoming ? "Upcoming" : "Operational"}
    </span>
  );
}
